import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { CyclingEvent, EventEdition, formatDayShort } from '@radevents/shared';
import EventZeile from '../EventZeile';

/**
 * Die Events eines Veranstalters — die Leseansicht der Veranstalterseite.
 *
 * **Seit 30.10.2026 dieselben Zeilen wie die Startseite** (`EventZeile`): Datumskachel in der
 * Disziplinfarbe, Anmeldestand, Strecken, Freunde und Merken. Vorher stand hier eine eigene
 * Saison-Linie mit Punkten und Karten, die nichts davon zeigte; wer vom Verein kam, sah ein
 * anderes Event als auf der Startseite. Vorbild Luma und Posh: die Events eines Gastgebers in der
 * Form, in der man sie auch sonst findet.
 *
 * **Vergangene stehen unter den kommenden**, eingeklappt und schlicht (Datum, Titel, Ort):
 * wer die Seite aufruft, will wissen, wo er mitfahren kann. Ganz weglassen geht nicht, sonst
 * sähe ein Verein ohne aktuellen Termin aus, als hätte er nie einen gehabt. Frühere Auflagen
 * (`event_editions`) reihen sich nach Datum zwischen die vergangenen Events.
 *
 * Bewusst **nicht** mit der Pflege-Zeile aus `manage/EventsTab` zusammengelegt: die trägt Chips,
 * Buchungen und Wirkungszahlen.
 */
interface Props {
  upcoming: CyclingEvent[];
  past: CyclingEvent[];
  editions?: EventEdition[];
  loading: boolean;
  /** Vergangene von vornherein aufgeklappt. Fürs Design-Lab. */
  initialShowPast?: boolean;
  /** Merken aus der Zeile. Ohne Handler (Design-Lab) bleibt der Knopf ohne Wirkung. */
  onFavorite?: (event: CyclingEvent) => void;
}

interface PastRow {
  key: string;
  eventId: number;
  title: string;
  eventDate: string;
  city?: string | null;
  kms: number[];
  edition: boolean;
  cancelled: boolean;
}

const pastOfEvent = (ev: CyclingEvent): PastRow => ({
  key: `e${ev.id}`, eventId: ev.id, title: ev.title, eventDate: ev.eventDate, city: ev.city,
  kms: ev.distances?.map(d => d.distanceKm).filter(k => k > 0).sort((a, b) => a - b) ?? [],
  edition: false, cancelled: !!ev.cancelled,
});

const pastOfEdition = (ed: EventEdition): PastRow => ({
  key: `a${ed.id}`, eventId: ed.eventId, title: ed.title, eventDate: ed.eventDate, city: ed.city,
  kms: ed.distanceKms, edition: true, cancelled: ed.cancelled,
});

export default function OrganizerSeason({ upcoming, past, editions = [], loading, initialShowPast, onFavorite }: Props) {
  // `null` heißt „noch nicht angefasst": dann entscheidet die Lage. Ohne kommendes Event steht die
  // Vergangenheit von selbst offen. Abgeleitet statt als Anfangswert, weil `upcoming` beim ersten
  // Rendern noch lädt und leer ist.
  const [manual, setManual] = useState<boolean | null>(initialShowPast ?? null);
  const showPast = manual ?? (!loading && upcoming.length === 0);
  // Jüngstes zuerst: aufgeklappt unter den kommenden liest sich die Liste als Blick zurück.
  const gone = [...past.map(pastOfEvent), ...editions.map(pastOfEdition)]
    .sort((a, b) => b.eventDate.localeCompare(a.eventDate));

  return (
    <section className="org-events" aria-labelledby="org-events-titel">
      <h2 id="org-events-titel" className="org-abschnitt">
        Kommende Events{!loading && upcoming.length > 0 && <span className="org-abschnitt-zahl">{upcoming.length}</span>}
      </h2>

      {loading ? (
        <div className="org-zeilen" aria-busy>
          {[0, 1].map(i => <div key={i} className="org-zeile-platzhalter" />)}
        </div>
      ) : upcoming.length === 0 ? (
        // Zwei Sätze, weil leer hier zweierlei heißt: „gerade nichts geplant" ist etwas anderes
        // als „hier war noch nie etwas".
        <p className="org-leer">
          {gone.length > 0 ? 'Gerade steht kein Event an.' : 'Noch keine Events eingetragen.'}
        </p>
      ) : (
        <div className="org-zeilen">
          {upcoming.map(ev => (
            <EventZeile
              key={ev.id}
              event={ev}
              favorited={!!ev.favorited}
              onFavorite={e => { e.stopPropagation(); onFavorite?.(ev); }}
              to={`/events/${ev.id}`}
            />
          ))}
        </div>
      )}

      {!loading && gone.length > 0 && (
        <div className="org-vergangen">
          <button type="button" className="org-vergangen-knopf" aria-expanded={showPast} onClick={() => setManual(!showPast)}>
            Vergangene Events <span className="org-abschnitt-zahl">{gone.length}</span>
            <ChevronDown size={16} aria-hidden className={showPast ? 'org-pfeil org-pfeil--offen' : 'org-pfeil'} />
          </button>
          {showPast && (
            <ul className="org-vergangen-liste ui-aufklappen">
              {gone.map(row => (
                <li key={row.key}>
                  <Link to={`/events/${row.eventId}`} className="org-vergangen-zeile">
                    <span className="org-vergangen-datum">{formatDayShort(row.eventDate)}</span>
                    <span className="org-vergangen-text">
                      <span className={row.cancelled ? 'org-vergangen-titel org-vergangen-titel--abgesagt' : 'org-vergangen-titel'}>{row.title}</span>
                      <span className="org-vergangen-meta">
                        {[row.city, row.kms.length ? `${row.kms.map(k => Math.round(k)).join(' · ')} km` : null,
                          row.edition ? 'frühere Auflage' : null, row.cancelled ? 'abgesagt' : null].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
